import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Autocomplete,
  TextField,
  Typography,
  Box,
  Paper,
  Grow,
} from "@mui/material";
import MDButton from "components/MDButton";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { copyAssignRate } from "store/slice/rates/rateSlice";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import NewConfirmationDialog from "./ConfirmationDialog";
import { useMaterialUIController } from "context";
import MDDialog from "components/MDDialog";
import MDBox from "components/MDBox";

const CopyRatesDialog = ({
  dialogOpen,
  onClose,
  fromPlaceId,
  placeList = [],
}) => {
  const dispatch = useDispatch();
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const fromPlaceObj = useMemo(
    () => placeList.find((p) => p._id === fromPlaceId) || null,
    [placeList, fromPlaceId]
  );

  const validationSchema = Yup.object().shape({
    toPlaceId: Yup.string().required("Please select a place"),
  });

  const formik = useFormik({
    initialValues: { toPlaceId: "" },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: () => setConfirmOpen(true),
  });

  const selectedToPlaceObj = useMemo(
    () => placeList.find((p) => p._id === formik.values.toPlaceId) || null,
    [placeList, formik.values.toPlaceId]
  );

  const handleConfirm = async () => {
    setConfirmOpen(false);
    setIsCopying(true);

    const payload = {
      fromPlaceId,
      toPlaceId: formik.values.toPlaceId,
    };

    try {
      const res = await dispatch(copyAssignRate(payload)).unwrap();

      if (res?.success) {
        setCopySuccess(true);
        toast.success("Rates copied successfully!");

        setTimeout(() => {
          setIsCopying(false);
          setCopySuccess(false);
          onClose();
          formik.resetForm();
        }, 1300);
      } else {
        toast.error(res?.message || "Failed to copy rates");
        setIsCopying(false);
      }
    } catch {
      toast.error("Something went wrong!");
      setIsCopying(false);
    }
  };

  const toPlaceOptions = useMemo(
    () => placeList.filter((p) => p._id !== fromPlaceId),
    [placeList, fromPlaceId]
  );

  return (
    <>
      <MDDialog
        dialogTitle="Copy Rates & Assign"
        open={dialogOpen}
        dialogClose={onClose}
        maxWidth="sm"
      >
        <DialogContent>
          {isCopying ? (
            <Box
              sx={{
                py: 5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              {!copySuccess ? (
                <>
                  <Typography variant="body1" fontWeight={600}>
                    Copying rates...
                  </Typography>

                  <MDBox
                    sx={{
                      width: 240,
                      height: 10,
                      borderRadius: 5,
                      overflow: "hidden",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: "50%",
                        position: "absolute",
                        left: 0,
                        top: 0,
                        animation: "loadingSlide 1s infinite linear",
                        background:
                          "linear-gradient(90deg, #42a5f5, #64b5f6, #90caf9)",
                      }}
                    />
                  </MDBox>

                  <style>
                    {`
                      @keyframes loadingSlide {
                        0% { transform: translateX(-120%); }
                        100% { transform: translateX(200%); }
                      }
                    `}
                  </style>
                </>
              ) : (
                <>
                  <Typography
                    variant="body1"
                    fontWeight={700}
                    color="success.main"
                    sx={{ fontSize: 18 }}
                  >
                    Copied Successfully!
                  </Typography>

                  {/* SUCCESS CHECKMARK */}
                  <Box
                    sx={{
                      fontSize: 60,
                      animation: "successPop 0.4s ease-out",
                    }}
                  >
                    ✅
                  </Box>

                  <style>
                    {`
                      @keyframes successPop {
                        0% { transform: scale(0.4); opacity: 0; }
                        100% { transform: scale(1); opacity: 1; }
                      }

                      .confetti-box {
                        position: absolute;
                        width: 100%;
                        height: 100%;
                        top: 0;
                        left: 0;
                        pointer-events: none;
                        background-image: radial-gradient(#81c784 3px, transparent 3px),
                          radial-gradient(#4caf50 3px, transparent 3px);
                        background-size: 18px 18px;
                        background-position: 0 0, 9px 9px;
                        opacity: 0.2;
                        animation: confettiFall 1s ease-out forwards;
                      }

                      @keyframes confettiFall {
                        from { transform: translateY(-20px); opacity: 0.4; }
                        to { transform: translateY(10px); opacity: 0; }
                      }
                    `}
                  </style>
                </>
              )}
            </Box>
          ) : (
            <Grow in timeout={300}>
              <Paper
                elevation={1}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  background: "transparent",
                }}
              >
                <form onSubmit={formik.handleSubmit}>
                  <Grid container spacing={3}>
                    {/* FROM SECTION */}
                    <Grid item xs={12}>
                      <Typography
                        fontSize={13}
                        color="text.secondary"
                        sx={{ mb: 1 }}
                        textAlign="center"
                        fontWeight="bold"
                      >
                        Copy From Place
                      </Typography>

                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: "1px solid #e0e0e0",
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          width: "100%",
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <LocationOnIcon
                            sx={{ fontSize: 20, color: "#555" }}
                          />
                          <Typography fontSize={15} fontWeight={600}>
                            {fromPlaceObj?.parkingCode}
                          </Typography>
                        </Box>

                        <Typography fontSize={14}>
                          {fromPlaceObj?.google?.formatted_address}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* TO SECTION */}
                    <Grid item xs={12}>
                      <Typography
                        fontSize={13}
                        color="text.secondary"
                        sx={{ mb: 1 }}
                        textAlign="center"
                        fontWeight="bold"
                      >
                        To This Place
                      </Typography>

                      <Autocomplete
                        fullWidth
                        options={toPlaceOptions}
                        value={selectedToPlaceObj}
                        onChange={(e, val) =>
                          formik.setFieldValue("toPlaceId", val?._id || "")
                        }
                        getOptionLabel={(option) =>
                          `${option?.parkingCode} — ${option?.google?.formatted_address}`
                        }
                        isOptionEqualToValue={(o, v) => o._id === v._id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            size="medium"
                            placeholder="Select a destination place..."
                            error={Boolean(
                              formik.touched.toPlaceId &&
                                formik.errors.toPlaceId
                            )}
                            helperText={
                              formik.touched.toPlaceId &&
                              formik.errors.toPlaceId
                            }
                            sx={{
                              "& .MuiInputBase-root": {
                                height: 48,
                                fontSize: 15,
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* INFO NOTE */}
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ fontSize: 12 }}>
                        * Your already created rates & assigned rates will be
                        removed before copying.
                      </Typography>
                    </Grid>

                    {/* BUTTONS */}
                    <Grid
                      item
                      xs={12}
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1.5,
                        mt: 1,
                      }}
                    >
                      <MDButton
                        onClick={() => {
                          onClose();
                          formik.resetForm();
                        }}
                        variant="outlined"
                        color="dark"
                      >
                        Cancel
                      </MDButton>

                      <MDButton
                        type="submit"
                        color={sidenavColor}
                        disabled={!formik.values.toPlaceId}
                      >
                        Submit
                      </MDButton>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </Grow>
          )}
        </DialogContent>
      </MDDialog>

      {/* CONFIRMATION DIALOG */}
      <NewConfirmationDialog
        title="Confirm Copy"
        description="This will overwrite assigned rates. Do you want to continue?"
        dialogOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        loading={isCopying}
      />
    </>
  );
};

export default CopyRatesDialog;
